import { useEffect, useState } from "react";
import { FileSearch, Download, FileJson, FileCode, FileText } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Loader } from "@/components/common/Loader";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";

export default function Evidence() {
    const [packages, setPackages] = useState(null);
    const [active, setActive] = useState(null);

    useEffect(() => { api.get("/evidence").then(({ data }) => setPackages(data.packages)); }, []);

    const download = async (id, format) => {
        const { data } = await api.get(`/evidence/${id}/export`, { params: { format }, responseType: "blob" });
        const url = URL.createObjectURL(data);
        const link = document.createElement("a");
        link.href = url;
        link.download = `evidence_${id}.${format}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    if (!packages) return <Loader label="Loading evidence" />;

    return (
        <div className="ww-fade-up">
            <PageHeader title="Evidence Packages" subtitle="Documented encounters with timeline, analysis and confidence scores." />

            {packages.length === 0 ? (
                <EmptyState icon={FileSearch} title="No evidence packages" subtitle="Run an anomaly analysis on the Integrity page and create a package from a flagged player." />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {packages.map((p) => (
                        <div key={p.id} className="bg-surface border border-border rounded-sm p-5 hover:border-white/25 transition-colors cursor-pointer" onClick={() => setActive(p)} data-testid={`evidence-${p.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="font-heading font-bold text-white truncate">{p.title}</div>
                                    <div className="text-xs text-muted-foreground font-mono mt-0.5">{p.subject_player} · {p.game}</div>
                                </div>
                                <StatusBadge status={p.status} />
                            </div>
                            <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{p.analysis}</p>
                            <div className="flex items-center justify-between mt-4">
                                <span className="text-xs font-mono text-muted-foreground">Confidence <span className="text-volt">{p.confidence}%</span></span>
                                <span className="text-xs font-mono text-muted-foreground">{p.timeline?.length || 0} events</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
                <DialogContent className="bg-surface border-border max-w-xl max-h-[88vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-heading">{active?.title}</DialogTitle></DialogHeader>
                    {active && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-2">
                                <StatusBadge status={active.status} />
                                <span className="text-xs text-muted-foreground font-mono">{active.subject_player} · {active.confidence}% confidence</span>
                            </div>
                            <div className="bg-obsidian border border-border rounded-sm p-4">
                                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">AI Analysis</div>
                                <p className="text-sm text-foreground/90">{active.analysis}</p>
                            </div>
                            <div>
                                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Timeline</div>
                                <div className="space-y-2">
                                    {active.timeline?.map((t, i) => (
                                        <div key={i} className="flex gap-3 text-sm">
                                            <span className="font-mono text-volt shrink-0 w-12">{t.time}</span>
                                            <div><span className="text-white">{t.event}</span> <span className="text-muted-foreground">— {t.note}</span></div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2 border-t border-border pt-4">
                                <span className="text-xs text-muted-foreground uppercase tracking-wider self-center mr-1">Export:</span>
                                <button onClick={() => download(active.id, "pdf")} data-testid="export-pdf" className="h-9 px-3 rounded-sm border border-border hover:bg-white/5 text-sm flex items-center gap-1.5"><FileText className="w-4 h-4 text-alert" /> PDF</button>
                                <button onClick={() => download(active.id, "html")} data-testid="export-html" className="h-9 px-3 rounded-sm border border-border hover:bg-white/5 text-sm flex items-center gap-1.5"><FileCode className="w-4 h-4 text-warn" /> HTML</button>
                                <button onClick={() => download(active.id, "json")} data-testid="export-json" className="h-9 px-3 rounded-sm border border-border hover:bg-white/5 text-sm flex items-center gap-1.5"><FileJson className="w-4 h-4 text-volt" /> JSON</button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
